import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { lumi } from '../../lib/lumi';

interface BudgetItem {
  category: string;
  amount: number;
}

interface SavedBudget {
  _id: string;
  destination: string;
  days: number;
  accommodation: number;
  food: number;
  activities: number;
  transport: number;
  miscellaneous: number;
  totalPerDay: number;
  totalTrip: number;
  createdAt: string;
  updatedAt: string;
}

export default function BudgetCalculator() {
  const [destination, setDestination] = useState('');
  const [days, setDays] = useState(1);
  const [accommodation, setAccommodation] = useState(0);
  const [food, setFood] = useState(0);
  const [activities, setActivities] = useState(0);
  const [transport, setTransport] = useState(0);
  const [miscellaneous, setMiscellaneous] = useState(0);
  
  const [savedBudgets, setSavedBudgets] = useState<SavedBudget[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const destinations = [
    { name: 'Tokyo, Japan', avgDaily: 150 },
    { name: 'Osaka, Japan', avgDaily: 130 },
    { name: 'Kyoto, Japan', avgDaily: 120 },
    { name: 'Singapore', avgDaily: 140 },
    { name: 'Hong Kong', avgDaily: 135 },
    { name: 'Orlando, USA', avgDaily: 180 },
    { name: 'Anaheim, USA', avgDaily: 190 },
  ];

  const totalPerDay = accommodation + food + activities + transport + miscellaneous;
  const totalTrip = totalPerDay * days;

  // Fetch saved budgets on mount
  useEffect(() => {
    fetchSavedBudgets();
  }, []);

  const fetchSavedBudgets = async () => {
    setLoading(true);
    try {
      const { list } = await lumi.entities.budget_calculations.list({
        sort: { createdAt: -1 },
        limit: 10
      });
      setSavedBudgets(list);
    } catch (error) {
      console.error('Failed to fetch saved budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async () => {
    if (!destination || totalTrip === 0) {
      setSaveMessage('Please select a destination and add expenses before saving');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setLoading(true);
    try {
      await lumi.entities.budget_calculations.create({
        destination,
        days,
        accommodation,
        food,
        activities,
        transport,
        miscellaneous,
        totalPerDay,
        totalTrip,
        creator: 'user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      setSaveMessage('Budget saved successfully! 💾');
      setTimeout(() => setSaveMessage(''), 3000);
      await fetchSavedBudgets();
    } catch (error) {
      console.error('Failed to save budget:', error);
      setSaveMessage('Failed to save budget. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const loadBudget = (budget: SavedBudget) => {
    setDestination(budget.destination);
    setDays(budget.days);
    setAccommodation(budget.accommodation);
    setFood(budget.food);
    setActivities(budget.activities);
    setTransport(budget.transport);
    setMiscellaneous(budget.miscellaneous);
    setShowSaved(false);
    setSaveMessage('Budget loaded! 📂');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const deleteBudget = async (id: string) => {
    if (typeof id !== 'string') {
      console.error('Invalid ID: must be a string');
      return;
    }

    setLoading(true);
    try {
      await lumi.entities.budget_calculations.delete(id);
      setSaveMessage('Budget deleted ✓');
      setTimeout(() => setSaveMessage(''), 3000);
      await fetchSavedBudgets();
    } catch (error) {
      console.error('Failed to delete budget:', error);
      setSaveMessage('Failed to delete budget');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFill = (destName: string) => {
    const dest = destinations.find(d => d.name === destName);
    if (dest) {
      setDestination(destName);
      setAccommodation(dest.avgDaily * 0.35);
      setFood(dest.avgDaily * 0.25);
      setActivities(dest.avgDaily * 0.25);
      setTransport(dest.avgDaily * 0.10);
      setMiscellaneous(dest.avgDaily * 0.05);
    }
  };

  const resetCalculator = () => {
    setDestination('');
    setDays(1);
    setAccommodation(0);
    setFood(0);
    setActivities(0);
    setTransport(0);
    setMiscellaneous(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-white py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/tools" className="text-[#4A90E2] hover:text-[#FF6B35] transition-colors mb-4 inline-block">
            ← Back to Tools
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-4">
                Travel Budget Calculator
              </h1>
              <p className="text-gray-600 text-lg">
                Plan your trip expenses with our easy-to-use budget calculator
              </p>
            </div>
            <button
              onClick={() => setShowSaved(!showSaved)}
              className="hidden md:flex items-center gap-2 px-6 py-3 bg-[#4A90E2] text-white rounded-lg hover:bg-[#FF6B35] transition-all shadow-lg"
            >
              <span>📂</span>
              <span>Saved Budgets ({savedBudgets.length})</span>
            </button>
          </div>
          
          {/* Save Message */}
          {saveMessage && (
            <div className="mt-4 p-4 bg-[#4A90E2]/10 border border-[#4A90E2] rounded-lg text-[#0A2540] font-medium">
              {saveMessage}
            </div>
          )}
        </div>

        {/* Mobile Saved Budgets Button */}
        <button
          onClick={() => setShowSaved(!showSaved)}
          className="md:hidden w-full mb-4 flex items-center justify-center gap-2 px-6 py-3 bg-[#4A90E2] text-white rounded-lg hover:bg-[#FF6B35] transition-all shadow-lg"
        >
          <span>📂</span>
          <span>Saved Budgets ({savedBudgets.length})</span>
        </button>

        {/* Saved Budgets Panel */}
        {showSaved && (
          <div className="mb-8 bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#0A2540]">Your Saved Budgets</h2>
              <button
                onClick={() => setShowSaved(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            {savedBudgets.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No saved budgets yet. Create your first budget and save it!</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {savedBudgets.map((budget) => (
                  <div
                    key={budget._id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-[#4A90E2] transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-[#0A2540] text-lg">{budget.destination}</h3>
                        <p className="text-sm text-gray-500">
                          {budget.days} {budget.days === 1 ? 'day' : 'days'} • 
                          {new Date(budget.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#FF6B35]">
                          ${budget.totalTrip.toFixed(0)}
                        </div>
                        <div className="text-xs text-gray-500">
                          ${budget.totalPerDay.toFixed(0)}/day
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadBudget(budget)}
                        className="flex-1 px-4 py-2 bg-[#4A90E2] text-white rounded-lg hover:bg-[#FF6B35] transition-colors text-sm"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => deleteBudget(budget._id)}
                        className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {/* Input Section */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              {/* Destination Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Select Destination
                </label>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                >
                  <option value="">Choose a destination...</option>
                  {destinations.map((dest) => (
                    <option key={dest.name} value={dest.name}>
                      {dest.name}
                    </option>
                  ))}
                </select>
                {destination && (
                  <button
                    onClick={() => handleQuickFill(destination)}
                    className="mt-2 text-sm text-[#4A90E2] hover:text-[#FF6B35] transition-colors"
                  >
                    Auto-fill with average costs
                  </button>
                )}
              </div>

              {/* Trip Duration */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Trip Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={days}
                  onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                />
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Daily Expenses</h3>

                {/* Accommodation */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accommodation (per night)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={accommodation}
                      onChange={(e) => setAccommodation(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Food */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Food & Drinks (per day)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={food}
                      onChange={(e) => setFood(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Activities */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activities & Attractions (per day)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={activities}
                      onChange={(e) => setActivities(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Transport */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Transport (per day)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={transport}
                      onChange={(e) => setTransport(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Miscellaneous */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Miscellaneous (per day)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={miscellaneous}
                      onChange={(e) => setMiscellaneous(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={resetCalculator}
                className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset Calculator
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="md:col-span-1">
            <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5a] rounded-2xl shadow-lg p-6 text-white sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Your Budget</h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-sm opacity-90">Accommodation</span>
                  <span className="font-semibold">${accommodation.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-sm opacity-90">Food & Drinks</span>
                  <span className="font-semibold">${food.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-sm opacity-90">Activities</span>
                  <span className="font-semibold">${activities.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-sm opacity-90">Transport</span>
                  <span className="font-semibold">${transport.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-sm opacity-90">Miscellaneous</span>
                  <span className="font-semibold">${miscellaneous.toFixed(2)}</span>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
                <div className="text-sm opacity-90 mb-1">Daily Total</div>
                <div className="text-3xl font-bold">${totalPerDay.toFixed(2)}</div>
              </div>

              <div className="bg-[#FF6B35] rounded-lg p-4">
                <div className="text-sm opacity-90 mb-1">Trip Total ({days} {days === 1 ? 'day' : 'days'})</div>
                <div className="text-3xl font-bold">${totalTrip.toFixed(2)}</div>
              </div>

              {totalTrip > 0 && (
                <div className="mt-6 p-4 bg-white/10 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Budget Breakdown</h4>
                  <div className="space-y-1 text-xs">
                    {accommodation > 0 && (
                      <div className="flex justify-between">
                        <span>Accommodation:</span>
                        <span>{((accommodation / totalPerDay) * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {food > 0 && (
                      <div className="flex justify-between">
                        <span>Food:</span>
                        <span>{((food / totalPerDay) * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {activities > 0 && (
                      <div className="flex justify-between">
                        <span>Activities:</span>
                        <span>{((activities / totalPerDay) * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-[#F7B32B]/10 border border-[#F7B32B]/30 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-[#0A2540] mb-3 flex items-center">
            <span className="text-2xl mr-2">💡</span>
            Budget Tips
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#FF6B35] mr-2">•</span>
              <span>Add 10-20% buffer for unexpected expenses</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF6B35] mr-2">•</span>
              <span>Research free activities and attractions at your destination</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF6B35] mr-2">•</span>
              <span>Consider transportation passes for multi-day savings</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#FF6B35] mr-2">•</span>
              <span>Book attractions and tickets in advance for better deals</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
